/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    getCatalogId,
    getAdditionalMediaLocationsForProductAction,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICreateActionContext,
    IGeneric
} from '@msdyn365-commerce/core';
import { AdditionalMediaLocationsInput } from '@msdyn365-commerce/action-internal';
import { AsyncResult, MediaLocation, SimpleProduct } from '@msdyn365-commerce/retail-proxy';

import getSelectedVariant, { SelectedVariantInput } from './get-selected-variant';
import { getSelectedProductIdFromActionInput } from './utilities';

/**
 * The action input class for the getAdditionalMediaLocationsForSelectedVariant data action.
 */
export class AdditionalMediaLocationsForSelectedVariantInput implements IActionInput {
    public productId: number;

    public channelId: number;

    public catalogId: number;

    public selectedProduct: SimpleProduct | undefined;

    constructor(productId: number, channelId: number, selectedProduct?: SimpleProduct, catalogId?: number) {
        this.productId = productId;
        this.channelId = channelId;
        this.catalogId = catalogId || 0;
        this.selectedProduct = selectedProduct;
    }

    public getCacheKey = () => 'AdditionalMediaLocationsForSelectedVariant';

    public getCacheObjectType = () => 'MediaLocations';

    public dataCacheType = (): CacheType => 'none';
}

/**
 * The createInput method for the getAdditionalMediaLocationsForSelectedVariant data action.
 * @param inputData The input data passed to the createInput method.
 */
export const createMediaLocationsForSelectedVariantInput = (
    inputData: ICreateActionContext<IGeneric<IAny>>
): AdditionalMediaLocationsForSelectedVariantInput => {
    const productId = getSelectedProductIdFromActionInput(inputData);
    const catalogId = getCatalogId(inputData.requestContext);

    if (productId) {
        return new AdditionalMediaLocationsForSelectedVariantInput(
            +productId,
            +inputData.requestContext.apiSettings.channelId,
            undefined,
            catalogId
        );
    }
    throw new Error('Unable to create AdditionalMediaLocationsForSelectedVariantInput, no productId found on module config or query');
};

/**
 * The action method for the getAdditionalMediaLocationsForSelectedVariant data action.
 * @param input
 * @param ctx
 */
export async function getAdditionalMediaLocationsForSelectedVariantAction(
    input: AdditionalMediaLocationsForSelectedVariantInput,
    ctx: IActionContext
): Promise<MediaLocation[]> {
    return AsyncResult.resolve()
        .then(() => {
            const activeProduct: SimpleProduct | undefined = input.selectedProduct;

            if (!activeProduct) {
                const selectedVariantInput = new SelectedVariantInput(
                    input.productId,
                    input.channelId,
                    undefined,
                    undefined,
                    ctx.requestContext
                );
                return getSelectedVariant(selectedVariantInput, ctx);
            }

            return activeProduct;
        })
        .then(productResult => {
            const activeProduct = productResult;

            if (activeProduct) {
                if (ctx.requestContext.app?.config?.OmniChannelMedia) {
                    const mediaLocationsInput = new AdditionalMediaLocationsInput(
                        ctx.requestContext,
                        activeProduct.RecordId,
                        input.channelId,
                        input.catalogId
                    );
                    return getAdditionalMediaLocationsForProductAction(mediaLocationsInput, ctx);
                }
            }

            return [];
        })
        .catch(error => {
            ctx.trace(error);
            ctx.telemetry.error(error.message);
            ctx.telemetry.debug('[getAdditionalMediaLocationsForSelectedVariantAction]Error executing action');
            throw new Error('[getAdditionalMediaLocationsForSelectedVariantAction]Error executing action');
        });
}

/**
 * The getAdditionalMediaLocationsForSelectedVariant data action
 * Gets the currently selected variant for the page via the getSelectedVariant data action,
 * and then gets the media location information for the variant via the MediaLocations RetailServer API.
 */
export const getAdditionalMediaLocationsForSelectedVariantActionDataAction = createObservableDataAction({
    id: '@msdyn365-commerce-modules/retail-actions/get-additional-media-locations-for-selected-variant',
    action: <IAction<MediaLocation[] | null>>getAdditionalMediaLocationsForSelectedVariantAction,
    input: createMediaLocationsForSelectedVariantInput
});

export default getAdditionalMediaLocationsForSelectedVariantActionDataAction;
